import { useState, useRef } from "react";
import { api, API } from "@/lib/api";

/** Modal to add images: either AI-generated (Nano Banana) or upload from disk.
 *  Props: projectId, onAdd(image), onClose, sectionKey (for prompt context)
 */
export default function ImageStudioModal({ projectId, sectionLabel, onAdd, onClose, suggestPrompt }) {
  const [prompt, setPrompt] = useState(suggestPrompt || "");
  const [n, setN] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [generated, setGenerated] = useState([]);
  const fileRef = useRef(null);

  const run = async () => {
    setLoading(true); setError(""); setGenerated([]);
    try {
      const r = await api.post(`/icaa/projects/${projectId}/ai/images`,
        { prompt, n, aspect: "16:9" },
        { timeout: 180000 });
      setGenerated(r.data.urls || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setLoading(true); setError("");
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onAdd({ url: r.data.url, source: "upload", prompt: "" });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const insert = (url) => onAdd({ url, source: "ai", prompt });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 p-4"
         onClick={onClose} data-testid="image-studio-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-[#FAFAF6] shadow-2xl">
        <header className="flex items-center justify-between border-b border-stone-300/60 px-6 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#26775E]">Imagen · Nano Banana / Subir</p>
            <h2 className="mt-0.5 font-serif text-2xl">{sectionLabel}</h2>
          </div>
          <button onClick={onClose} data-testid="image-modal-close"
                  className="rounded-full p-2 text-stone-500 hover:bg-stone-200">×</button>
        </header>

        <div className="grid flex-1 grid-cols-1 gap-0 overflow-hidden md:grid-cols-2">
          {/* Generate */}
          <div className="border-r border-stone-300/60 p-6">
            <h3 className="font-serif text-lg">Generar con IA</h3>
            <p className="mt-1 text-xs text-stone-500">Describe la imagen que necesitas. Genera 1-4 propuestas en 16:9.</p>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="image-prompt"
                      rows={5} placeholder="Ej: Plano cenital de una cala turquesa al amanecer, con restos de plástico semienterrados en la arena, luz dorada, estilo documental."
                      className="mt-3 w-full rounded-md border border-stone-300 bg-white p-3 text-sm outline-none focus:border-[#26775E]" />
            <div className="mt-3 flex items-center gap-3">
              <label className="text-xs uppercase tracking-widest text-stone-500">Nº propuestas</label>
              <select value={n} onChange={(e) => setN(Number(e.target.value))} data-testid="image-n"
                      className="rounded border border-stone-300 bg-white px-2 py-1 text-sm">
                {[1, 2, 3, 4].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <button onClick={run} disabled={loading || !prompt.trim()} data-testid="image-generate-btn"
                      className="ml-auto rounded-full bg-stone-900 px-5 py-2 text-xs uppercase tracking-widest text-[#FAFAF6] hover:bg-[#26775E] disabled:opacity-50">
                {loading ? "Generando…" : "Generar"}
              </button>
            </div>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 grid grid-cols-2 gap-3" data-testid="image-results">
              {generated.map((url, i) => (
                <button key={url} onClick={() => insert(url)} data-testid={`image-pick-${i}`}
                        className="group relative overflow-hidden rounded-md ring-1 ring-stone-300 hover:ring-2 hover:ring-[#26775E]">
                  <img src={`${API.replace('/api','')}${url}`} alt="" className="aspect-video w-full object-cover" />
                  <span className="absolute inset-x-0 bottom-0 translate-y-full bg-stone-900/80 px-2 py-1 text-center text-[10px] uppercase tracking-widest text-white transition group-hover:translate-y-0">
                    Añadir a {sectionLabel}
                  </span>
                </button>
              ))}
              {!generated.length && !loading && (
                <p className="col-span-2 text-center text-xs text-stone-400">Sin resultados todavía</p>
              )}
            </div>
          </div>

          {/* Upload */}
          <div className="p-6">
            <h3 className="font-serif text-lg">Subir mis fotos</h3>
            <p className="mt-1 text-xs text-stone-500">Sube tus propias imágenes (jpg, png, webp). Se añadirán a esta sección.</p>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} hidden data-testid="image-upload-input" />
            <button onClick={() => fileRef.current?.click()} data-testid="image-upload-btn"
                    className="mt-4 flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 bg-white py-12 text-center transition hover:border-[#26775E]">
              <svg className="h-8 w-8 text-stone-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 16V4M12 4l-4 4M12 4l4 4M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-sm text-stone-600">Haz clic para seleccionar imágenes</span>
              <span className="text-[10px] uppercase tracking-widest text-stone-400">o arrastra aquí</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
